/**
 * Help Glossary — mobile-safe bottom sheet, always closeable.
 *
 * Phase 3.5.7 P0: was rendering with no max-height, so on mobile the
 * 10-row content overflowed the viewport and the CLOSE button could
 * scroll out of reach. Now:
 *   • Sheet capped at max-height: 85dvh.
 *   • Sticky header with a permanently-visible CLOSE button
 *     (data-testid="help-close-button").
 *   • Scrollable inner body (data-testid="help-sheet-scroll") with
 *     `overscroll-behavior: contain` so scroll never escapes the sheet.
 *   • Backdrop tap closes.
 *   • Escape key closes (desktop).
 *
 * Copy still sourced from `tutorialConfig.GLOSSARY` — DRAW-first model
 * preserved unchanged from Phase 3.5.6.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { GLOSSARY, JOKER_EXPLAINER, DOUBLE_CHANCE_EXPLAINER } from '@/lib/tutorialConfig';

export default function HelpGlossary({ onClose }) {
  const [view, setView] = useState('list'); // 'list' | 'joker' | 'dc'

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-testid="tutorial-glossary"
      className="fixed inset-0 z-[58] bg-black/70 backdrop-blur-sm flex flex-col items-stretch justify-end"
      onClick={onClose}
    >
      <motion.div
        data-testid="help-sheet"
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="mx-auto w-full max-w-[440px] rounded-t-2xl border-t border-[hsl(var(--k-gold)/0.35)] bg-[hsl(220,12%,8%)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxHeight: '85dvh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Sticky header — Close button is ALWAYS visible. */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10 bg-[hsl(220,12%,8%)] rounded-t-2xl">
          <div className="font-heading text-lg tracking-[0.18em] text-white uppercase">
            {view === 'list' ? 'Help' : view === 'joker' ? 'Joker' : 'Double Chance'}
          </div>
          <div className="flex items-center gap-1">
            {view !== 'list' && (
              <button
                className="font-heading text-[10px] tracking-[0.28em] text-white/55 hover:text-white px-2 py-1.5"
                onClick={() => setView('list')}
              >
                BACK
              </button>
            )}
            <button
              data-testid="help-close-button"
              aria-label="Close help"
              className="w-9 h-9 rounded-full flex items-center justify-center text-white/75 hover:text-white border border-white/15 active:scale-95 transition-transform"
              onClick={onClose}
            >
              <X size={18} strokeWidth={2.25} />
            </button>
          </div>
        </div>

        {/* Scrollable body. */}
        <div
          data-testid="help-sheet-scroll"
          className="flex-1 overflow-y-auto px-5 py-4"
          style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          <AnimatePresence mode="wait">
            {view === 'list' && (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                {GLOSSARY.map((entry) => {
                  const isJoker = entry.term === 'JOKER';
                  const isDC = entry.term === 'DOUBLE CHANCE';
                  const linkable = isJoker || isDC;
                  return (
                    <button
                      key={entry.term}
                      onClick={linkable ? () => setView(isJoker ? 'joker' : 'dc') : undefined}
                      className={`w-full text-left rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 transition-colors ${linkable ? 'hover:border-[hsl(var(--k-gold)/0.5)] active:scale-[0.99]' : ''}`}
                      data-testid={`glossary-row-${entry.term.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="font-heading text-xs tracking-[0.2em] text-[hsl(var(--k-gold-bright))] uppercase">
                        {entry.term}
                      </div>
                      <div className="space-y-0.5 mt-0.5">
                        {entry.lines.map((line, idx) => (
                          <div key={idx} className="font-body text-sm text-white/80 leading-snug">
                            {line}
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </motion.div>
            )}

            {view === 'joker' && <JokerExplainerBody />}
            {view === 'dc' && <DoubleChanceExplainerBody />}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export function JokerExplainerBody() {
  return (
    <motion.div
      key="joker"
      data-testid={JOKER_EXPLAINER.testid}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="space-y-3"
    >
      <div className="font-body text-sm text-white/80">{JOKER_EXPLAINER.intro}</div>
      <div className="space-y-2">
        {JOKER_EXPLAINER.options.map((opt) => (
          <div
            key={opt.label}
            className="rounded-lg border border-[hsl(var(--k-gold)/0.25)] bg-white/[0.03] px-3 py-2"
          >
            <div className="font-heading text-xs tracking-[0.2em] text-[hsl(var(--k-gold-bright))] uppercase">
              {opt.label}
            </div>
            <div className="font-body text-sm text-white/80 mt-0.5">{opt.detail}</div>
          </div>
        ))}
      </div>
      <div className="pt-1 space-y-1 border-t border-white/10">
        {JOKER_EXPLAINER.footer.map((line, idx) => (
          <div key={idx} className="font-body text-xs text-white/65 leading-snug pt-1">
            {line}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export function DoubleChanceExplainerBody() {
  return (
    <motion.div
      key="dc"
      data-testid={DOUBLE_CHANCE_EXPLAINER.testid}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="space-y-3"
    >
      <div className="font-body text-sm text-white/80">{DOUBLE_CHANCE_EXPLAINER.intro}</div>
      <div className="space-y-1">
        {DOUBLE_CHANCE_EXPLAINER.body.map((line, idx) => (
          <div key={idx} className="font-body text-sm text-white/85">{line}</div>
        ))}
      </div>
      <div className="pt-1 space-y-1 border-t border-white/10">
        {DOUBLE_CHANCE_EXPLAINER.clarification.map((line, idx) => (
          <div key={idx} className="font-body text-xs text-white/65 leading-snug pt-1">
            {line}
          </div>
        ))}
      </div>
    </motion.div>
  );
}
